import { withStyleNonce } from '@/lib/csp-nonce';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  Copy,
  FileDashed,
  Layout,
  PencilSimple,
  ArrowsClockwise,
  BookmarkSimple,
} from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { TemplatePickerDrawer } from '@/modules/templates/components/template-picker-drawer/template-picker-drawer';
import { getTemplateById } from '@/modules/templates/services/templates.service';
import type { Template } from '@/modules/templates/types/templates.types';
import { getTemplateErrorMessage } from '@/modules/templates/utils/template-api-errors';
import {
  useGetCampaignsRest,
  useApplyCampaignTemplateRest,
  usePutCampaignContentRest,
} from '../../hooks/use-campaigns-rest';
import type { CampaignRestSummary } from '../../types/campaigns-rest.types';
import { blankCampaignHtml } from '../../utils/campaign-content.utils';
import { getCampaignContentRest } from '../../services/campaigns-rest.service';
import { useGetCampaignContentRest } from '../../hooks/use-campaigns-rest';
import { formatCampaignContentSavedLabel } from '../../utils/campaign-content-preview.utils';
import { deriveAutoPlainText } from '../../utils/campaign-content-meta.utils';
import {
  CampaignEmailBuilder,
  type CampaignEmailBuilderHandle,
} from '../campaign-email-builder/campaign-email-builder';
import { CampaignContentEditorHeader } from '../campaign-content-editor-header/campaign-content-editor-header';
import { CampaignContentPreviewDialog } from '../campaign-content-preview-dialog/campaign-content-preview-dialog';
import { CampaignTestSendDialog } from '../campaign-test-send-dialog/campaign-test-send-dialog';
import { Button } from '@/components/ui-kit/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui-kit/dialog';
import { Input } from '@/components/ui-kit/input';
import { Switch } from '@/components/ui-kit/switch';
import { Textarea } from '@/components/ui-kit/textarea';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

type ContentMode = 'chooser' | 'preview' | 'editor' | 'replace';

type CampaignContentStepProps = {
  campaignId?: string;
  campaignName: string;
  subject?: string;
  html: string;
  selectedTemplateId: string;
  selectedTemplateName?: string;
  savedAt?: string | null;
  onHtmlChange: (html: string) => void;
  onTemplateApplied: (payload: {
    html: string;
    templateId: string;
    templateName: string;
    contentId?: string | null;
    /** True when the server already has this content (apply-template / put). */
    synced?: boolean;
  }) => void;
  /** Parent hides wizard chrome while the full content editor is open (design: campaign-editor.html). */
  onEditorActiveChange?: (active: boolean) => void;
  /** Fired when HTML was written to the campaign on the server (put content). */
  onContentSynced?: (payload: { html: string; contentId?: string | null }) => void;
};

const StartOption = ({
  icon,
  title,
  help,
  onClick,
}: {
  icon: ReactNode;
  title: string;
  help: string;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className="flex items-start gap-3 rounded-lg border-2 border-border bg-card p-4 text-left transition-colors hover:border-primary-600"
  >
    <span className="grid h-9 w-9 flex-none place-items-center rounded-full bg-primary-50 text-primary-600">
      {icon}
    </span>
    <span className="min-w-0">
      <span className="block font-medium text-foreground">{title}</span>
      <span className="mt-0.5 block text-xs text-muted-foreground">{help}</span>
    </span>
  </button>
);

export const CampaignContentStep = ({
  campaignId,
  campaignName,
  subject = '',
  html,
  selectedTemplateId,
  selectedTemplateName = '',
  savedAt = null,
  onHtmlChange,
  onTemplateApplied,
  onEditorActiveChange,
  onContentSynced,
}: CampaignContentStepProps) => {
  const hasContent = Boolean(html.trim());
  const hasExistingTemplate = Boolean(selectedTemplateId.trim());
  const templateActionLabel = hasExistingTemplate ? 'Update template' : 'Save as template';
  const [mode, setMode] = useState<ContentMode>(hasContent ? 'preview' : 'chooser');
  const [editorSavedAt, setEditorSavedAt] = useState<string | null>(savedAt);
  const [manualPlain, setManualPlain] = useState(false);
  const [plainText, setPlainText] = useState('');
  const [isSavingContent, setIsSavingContent] = useState(false);
  const [pendingSaveAsTemplate, setPendingSaveAsTemplate] = useState(false);

  useEffect(() => {
    if (hasContent && mode === 'chooser') {
      setMode('preview');
    }
  }, [hasContent, mode]);

  useEffect(() => {
    onEditorActiveChange?.(mode === 'editor');
    return () => onEditorActiveChange?.(false);
  }, [mode, onEditorActiveChange]);

  useEffect(() => {
    setEditorSavedAt(savedAt);
  }, [savedAt]);

  const [copyOpen, setCopyOpen] = useState(false);
  const [copySearch, setCopySearch] = useState('');
  const [debouncedCopySearch, setDebouncedCopySearch] = useState('');
  const [templateOpen, setTemplateOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [testSendOpen, setTestSendOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const editorRef = useRef<CampaignEmailBuilderHandle>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [copyingCampaignId, setCopyingCampaignId] = useState<string | null>(null);
  const { data: serverContent } = useGetCampaignContentRest(campaignId, {
    enabled: Boolean(campaignId?.trim()),
  });
  const contentVersion = serverContent?.version || null;
  const { mutateAsync: putContent } = usePutCampaignContentRest();
  const { mutateAsync: applyTemplate } = useApplyCampaignTemplateRest();

  const persistHtml = async (nextHtml: string, plainText?: string | null) => {
    const html = nextHtml.trim();
    if (!campaignId?.trim()) {
      onHtmlChange(html);
      return { html, contentId: null as string | null, sanitizationRemoved: [] as string[] };
    }
    const saved = await putContent({
      campaignId,
      input: { html, plainText: plainText === undefined ? null : plainText },
    });
    onHtmlChange(saved.html);
    onContentSynced?.({
      html: saved.html,
      contentId: saved.contentId || null,
    });
    return {
      html: saved.html,
      contentId: saved.contentId || null,
      sanitizationRemoved: saved.sanitization.removed,
    };
  };

  const notifySanitization = (removed: string[]) => {
    if (!removed.length) return;
    toast({
      title: 'Some markup was removed for safety',
      description: `The server stripped: ${removed.join(', ')}. The editor now shows the stored HTML.`,
    });
  };
  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedCopySearch(copySearch.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [copySearch]);

  useEffect(() => {
    if (!copyOpen) {
      setCopySearch('');
      setDebouncedCopySearch('');
    }
  }, [copyOpen]);

  useEffect(() => {
    if (mode !== 'editor' || !pendingSaveAsTemplate) return;
    const timer = window.setTimeout(() => {
      editorRef.current?.openSaveAsTemplate();
      setPendingSaveAsTemplate(false);
    }, 100);
    return () => window.clearTimeout(timer);
  }, [mode, pendingSaveAsTemplate]);

  const { data: campaignsPage, isLoading: copyCampaignsLoading } = useGetCampaignsRest(
    {
      pageNo: 1,
      pageSize: 25,
      q: debouncedCopySearch || undefined,
    },
    { enabled: copyOpen }
  );

  const copyCandidates = useMemo(() => {
    const items = campaignsPage?.items ?? [];
    return items.filter((item) => !campaignId || item.id !== campaignId);
  }, [campaignsPage?.items, campaignId]);

  const previewSrcDoc = useMemo(() => {
    if (!html.trim()) return '';
    return html.includes('<html')
      ? html
      : `<!doctype html><html><head><meta charset="utf-8"></head><body>${html}</body></html>`;
  }, [html]);

  const showChooser = () => setMode(hasContent ? 'replace' : 'chooser');

  const handleStartBlank = async () => {
    const next = blankCampaignHtml(campaignName);
    try {
      const saved = await persistHtml(next, null);
      // Blank content is no longer linked to any template, so the editor offers "Save as template".
      onTemplateApplied({
        html: saved.html,
        templateId: '',
        templateName: '',
        contentId: saved.contentId,
        synced: Boolean(campaignId?.trim()),
      });
      notifySanitization(saved.sanitizationRemoved);
      setEditorSavedAt(new Date().toISOString());
      setMode('preview');
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to save blank content',
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    }
  };

  const handleSelectTemplate = async (template: Template): Promise<void | { ok: false }> => {
    const body = template.html?.trim() || '';
    if (!body) {
      toast({
        variant: 'destructive',
        title: 'Template has no content',
        description: 'Pick a template that already has a design, or start blank.',
      });
      return { ok: false as const };
    }

    try {
      if (campaignId?.trim()) {
        const applied = await applyTemplate({
          campaignId,
          input: { templateId: template.itemId },
        });
        onTemplateApplied({
          html: applied.html,
          templateId: template.itemId,
          templateName: template.name.trim() || 'Untitled template',
          contentId: applied.contentId || template.contentId,
          synced: true,
        });
        notifySanitization(applied.sanitization.removed);
      } else {
        onTemplateApplied({
          html: body,
          templateId: template.itemId,
          templateName: template.name.trim() || 'Untitled template',
          contentId: template.contentId,
          synced: false,
        });
      }
      toast({
        title: 'Template applied',
        description: `Content copied from “${template.name.trim() || 'template'}”. Editing the template later won’t change this campaign.`,
      });
      setEditorSavedAt(new Date().toISOString());
      setMode('preview');
      return;
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to apply template',
        description: error instanceof Error ? error.message : 'Please try again.',
      });
      return { ok: false as const };
    }
  };

  const handleCopyCampaign = async (source: CampaignRestSummary) => {
    if (copyingCampaignId) return;
    if (!campaignId?.trim()) {
      toast({
        variant: 'destructive',
        title: 'Save campaign details first',
        description: 'Create the campaign before copying content from another campaign.',
      });
      return;
    }

    setCopyingCampaignId(source.id);
    try {
      const sourceContent = await getCampaignContentRest(source.id);
      if (!sourceContent.html.trim()) {
        // Fall back to the source campaign's template when content is empty.
        const templateId = source.templateIds[0]?.trim();
        if (!templateId) {
          throw new Error('That campaign has no content to copy.');
        }
        const template = await queryClient.fetchQuery({
          queryKey: ['template', templateId],
          queryFn: () => getTemplateById(templateId),
          staleTime: 30 * 1000,
        });
        await handleSelectTemplate(template);
        setCopyOpen(false);
        return;
      }

      const saved = await persistHtml(sourceContent.html, sourceContent.plainText);
      onTemplateApplied({
        html: saved.html,
        // Only the content is copied; the source campaign's template link must not follow it.
        templateId: '',
        templateName: '',
        contentId: saved.contentId,
        synced: Boolean(campaignId?.trim()),
      });
      notifySanitization(saved.sanitizationRemoved);
      setEditorSavedAt(new Date().toISOString());
      setMode('preview');
      setCopyOpen(false);
      toast({
        title: 'Content copied',
        description: `Copied from “${source.name.trim() || 'campaign'}”.`,
      });
    } catch (copyError) {
      toast({
        variant: 'destructive',
        title: 'Failed to copy campaign content',
        description:
          copyError instanceof Error
            ? copyError.message
            : getTemplateErrorMessage(copyError, 'Please try again.'),
      });
    } finally {
      setCopyingCampaignId(null);
    }
  };

  const openEditor = () => {
    if (!html.trim()) {
      toast({
        variant: 'destructive',
        title: 'No content yet',
        description: 'Add content before opening the editor.',
      });
      return;
    }
    setMode('editor');
  };

  const resolveLiveHtml = async (): Promise<string> => {
    const content = await editorRef.current?.exportContent();
    return content?.html?.trim() || html;
  };

  const handleSaveContent = async () => {
    setIsSavingContent(true);
    try {
      const nextHtml = (await resolveLiveHtml()).trim();
      if (!nextHtml) {
        toast({
          variant: 'destructive',
          title: 'Nothing to save',
          description: 'Export HTML from the editor before saving.',
        });
        return;
      }
      const plain = manualPlain ? plainText : null;
      const saved = await persistHtml(nextHtml, plain);
      if (!manualPlain) {
        setPlainText(deriveAutoPlainText(saved.html));
      }
      notifySanitization(saved.sanitizationRemoved);
      const now = new Date().toISOString();
      setEditorSavedAt(now);
      toast({
        variant: 'success',
        title: 'Content saved',
        description: 'This campaign’s content was updated.',
      });
      setMode('preview');
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to save content',
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    } finally {
      setIsSavingContent(false);
    }
  };

  if (mode === 'editor') {
    const titleName = campaignName.trim() || 'Untitled campaign';
    return (
      <div className="mx-auto flex w-full max-w-[1320px] flex-col gap-5 pb-8">
        <CampaignContentEditorHeader
          title={`Edit content — ${titleName}`}
          subtitle={
            html.trim()
              ? formatCampaignContentSavedLabel(
                  serverContent?.updatedAt || editorSavedAt || new Date().toISOString(),
                  contentVersion
                )
              : 'No content saved yet.'
          }
          onPreview={() => {
            void (async () => {
              const live = await resolveLiveHtml();
              setPreviewHtml(live);
              setPreviewOpen(true);
            })();
          }}
          onTestSend={() => setTestSendOpen(true)}
          onSaveAsTemplate={() => editorRef.current?.openSaveAsTemplate()}
          saveAsTemplateLabel={templateActionLabel}
          backAction={{
            label: 'Back to campaign',
            onClick: () => setMode('preview'),
          }}
        />

        <CampaignEmailBuilder
          ref={editorRef}
          html={html}
          name={campaignName || 'Draft'}
          existingTemplateId={selectedTemplateId}
          existingTemplateName={selectedTemplateName}
          showInlineActions={false}
          layout="design"
          onContentChange={() => undefined}
          onSave={(_json, nextHtml) => {
            onHtmlChange(nextHtml);
          }}
          onTemplateSaved={({ html: nextHtml, name, templateId, contentId }) => {
            onTemplateApplied({
              html: nextHtml,
              templateId,
              templateName: name,
              contentId,
            });
          }}
        />

        <div className="flex flex-col gap-3 rounded-lg border border-neutral-200 bg-white p-6 shadow-e1">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <label className="flex cursor-pointer items-center gap-2 text-[13px] text-neutral-700">
              <Switch
                checked={manualPlain}
                onCheckedChange={(checked) => {
                  setManualPlain(checked);
                  if (checked && !plainText.trim()) {
                    void (async () => {
                      const live = await resolveLiveHtml();
                      setPlainText(deriveAutoPlainText(live));
                    })();
                  }
                }}
              />
              Author plain text manually
            </label>
            <span className="text-[13px] text-neutral-500">
              {manualPlain
                ? 'Authored by you — no longer derived from the HTML.'
                : 'Derived from your HTML on every save.'}
            </span>
          </div>
          {manualPlain ? (
            <Textarea
              rows={6}
              placeholder="Plain-text version…"
              aria-label="Plain-text version of the email"
              value={plainText}
              onChange={(event) => setPlainText(event.target.value)}
            />
          ) : null}
        </div>

        <div className="flex items-center gap-3">
          <div className="flex-1" />
          <Button
            type="button"
            className="bg-primary-600 text-white hover:bg-primary-700"
            disabled={isSavingContent}
            onClick={() => {
              void handleSaveContent();
            }}
          >
            {isSavingContent ? 'Saving…' : 'Save content'}
          </Button>
        </div>

        <CampaignContentPreviewDialog
          open={previewOpen}
          onOpenChange={setPreviewOpen}
          html={previewHtml}
          plainText={manualPlain ? plainText : deriveAutoPlainText(previewHtml || html)}
        />
        <CampaignTestSendDialog
          open={testSendOpen}
          onOpenChange={setTestSendOpen}
          campaignId={campaignId}
          subject={subject}
        />
      </div>
    );
  }

  if (mode === 'preview' && hasContent) {
    return (
      <div className="mx-auto flex w-full flex-col gap-4 rounded-lg border border-neutral-200 bg-white p-6 shadow-e1">
        <h3 className="flex-none text-base font-semibold text-neutral-900">Content</h3>
        <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
          <div className="relative h-[150px] w-[220px] flex-none overflow-hidden rounded border border-neutral-200 bg-white">
            <iframe
              title="Campaign content preview"
              className="pointer-events-none absolute left-0 top-0 origin-top-left border-0"
              style={{ width: 600, height: 420, transform: 'scale(0.36)' }}
              sandbox=""
              srcDoc={withStyleNonce(previewSrcDoc)}
            />
          </div>
          <div className="flex min-w-[220px] flex-1 flex-col gap-2 lg:max-w-sm">
            <p className="text-sm text-neutral-900">
              {formatCampaignContentSavedLabel(serverContent?.updatedAt || editorSavedAt, contentVersion)}
            </p>
            <p className="text-[13px] text-neutral-500">
              Every save keeps the previous version, so nothing you replace is lost.
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Button
                type="button"
                className="bg-primary-600 text-white hover:bg-primary-700"
                onClick={openEditor}
              >
                <PencilSimple className="size-4" />
                Open editor
              </Button>
              <Button type="button" variant="outline" onClick={showChooser}>
                <ArrowsClockwise className="size-4" />
                Replace content
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setPendingSaveAsTemplate(true);
                  openEditor();
                }}
              >
                <BookmarkSimple className="size-4" />
                {templateActionLabel}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const replacing = mode === 'replace';

  return (
    <div className="mx-auto flex w-full flex-col space-y-4 rounded-lg border border-neutral-200 bg-white p-6 shadow-e1">
      <div>
        <h3 className="text-base font-semibold text-neutral-900">
          {replacing ? 'Replace content' : 'How should this email start?'}
        </h3>
        <p className="mt-1 text-[13px] text-neutral-500">
          {replacing
            ? 'The current version stays in this campaign’s history — replacing it adds a new version rather than overwriting one.'
            : 'You can change any of this later. Nothing here is final.'}
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <StartOption
          icon={<Layout className="h-[18px] w-[18px]" />}
          title="Use a template"
          help="Browse saved layouts and copy one in."
          onClick={() => setTemplateOpen(true)}
        />
        <StartOption
          icon={<Copy className="h-[18px] w-[18px]" />}
          title="Copy an earlier campaign"
          help="Reuse the content from something you already sent."
          onClick={() => setCopyOpen(true)}
        />
        <StartOption
          icon={<FileDashed className="h-[18px] w-[18px]" />}
          title="Start blank"
          help="An empty canvas in the editor."
          onClick={() => {
            void handleStartBlank();
          }}
        />
      </div>

      {replacing ? (
        <Button type="button" variant="ghost" size="sm" onClick={() => setMode('preview')}>
          Keep the current content
        </Button>
      ) : null}

      {!hasContent ? (
        <p className={cn('text-sm text-destructive')} role="alert">
          Add some content before continuing — pick a template, copy a campaign, or start blank.
        </p>
      ) : null}

      <TemplatePickerDrawer
        open={templateOpen}
        onOpenChange={setTemplateOpen}
        selectedId={selectedTemplateId || null}
        note="Copied into this campaign — the template itself is not linked."
        confirmLabel="Use template"
        onChoose={handleSelectTemplate}
      />

      <Dialog open={copyOpen} onOpenChange={setCopyOpen}>
        <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Copy content from a campaign</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Only the email content is copied. Audience, schedule and sender details stay as you set
            them here.
          </p>
          <Input
            type="search"
            value={copySearch}
            onChange={(event) => setCopySearch(event.target.value)}
            placeholder="Search campaigns"
            aria-label="Search campaigns"
          />
          <div className="flex max-h-[320px] flex-col gap-2 overflow-y-auto">
            {copyCampaignsLoading ? (
              <p className="text-sm text-muted-foreground">Loading campaigns…</p>
            ) : copyCandidates.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No other campaign has content to copy yet.
              </p>
            ) : (
              copyCandidates.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  disabled={Boolean(copyingCampaignId)}
                  className="rounded border border-border p-3 text-left hover:border-primary-600 disabled:opacity-60"
                  onClick={() => {
                    void handleCopyCampaign(item);
                  }}
                >
                  <span className="block font-medium">
                    {item.name.trim() || 'Untitled campaign'}
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    {copyingCampaignId === item.id ? 'Loading template…' : item.status || 'Draft'}
                  </span>
                </button>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
